import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { DisputeCaseType } from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { MediationService } from "./mediation.service";
import { DueProcessService } from "./due-process.service";
import { IEPFacilitationService } from "./iep-facilitation.service";
import { StateComplaintService } from "./state-complaint.service";
import { AreaOfConcernService } from "./area-of-concern.service";
import { ParentEngagementService } from "./parent-engagement.service";

interface DisputeCaseService {
  create(payload: unknown): Observable<unknown>;
  getById(id: string): Observable<unknown>;
  update(id: string, payload: unknown): Observable<unknown>;
  delete(id: string): Observable<unknown>;
  getAll(): Observable<unknown>;
  createParty(payload: unknown): Observable<unknown>;
  updateParty(id: string, payload: unknown): Observable<unknown>;
  getDocumentsByDisputeCase(disputeCaseId: string): Observable<unknown>;
  deleteDocument(id: string): Observable<unknown>;
  setDocumentPartyAccess(partyId: string, docId: string, canAccess: boolean): Observable<unknown>;
}

@Injectable({
  providedIn: "root",
})
export class DisputeCaseFactoryService {
  private serviceMap: Map<DisputeCaseType, DisputeCaseService>;

  constructor(
    private mediationService: MediationService,
    private iepFacilitationService: IEPFacilitationService,
    private dueProcessService: DueProcessService,
    private stateComplaintService: StateComplaintService,
    private areaOfConcernService: AreaOfConcernService,
    private parentEngagementService: ParentEngagementService,
  ) {
    this.serviceMap = new Map<DisputeCaseType, DisputeCaseService>([
      [DisputeCaseType.Mediation, this.mediationService as unknown as DisputeCaseService],
      [DisputeCaseType.IEPFacilitation, this.iepFacilitationService as unknown as DisputeCaseService],
      [DisputeCaseType.DueProcess, this.dueProcessService as unknown as DisputeCaseService],
      [DisputeCaseType.StateComplaint, this.stateComplaintService as unknown as DisputeCaseService],
      [DisputeCaseType.AreaOfConcern, this.areaOfConcernService as unknown as DisputeCaseService],
      [DisputeCaseType.ParentEngagement, this.parentEngagementService as unknown as DisputeCaseService],
      // Add more mappings as needed
    ]);
  }

  getService(caseType: DisputeCaseType): DisputeCaseService {
    const service = this.serviceMap.get(caseType);
    if (!service) {
      throw new Error(`No service found for dispute case type: ${caseType}`);
    }
    return service;
  }

  create(caseType: DisputeCaseType, payload: unknown) {
    return this.getService(caseType).create(payload);
  }

  getById(caseType: DisputeCaseType, id: string) {
    return this.getService(caseType).getById(id);
  }

  update(caseType: DisputeCaseType, id: string, payload: unknown) {
    return this.getService(caseType).update(id, payload);
  }

  delete(caseType: DisputeCaseType, id: string) {
    return this.getService(caseType).delete(id);
  }

  getAll(caseType: DisputeCaseType) {
    return this.getService(caseType).getAll();
  }

  createParty(caseType: DisputeCaseType, payload: unknown) {
    return this.getService(caseType).createParty(payload);
  }

  updateParty(caseType: DisputeCaseType, id: string, payload: unknown) {
    return this.getService(caseType).updateParty(id, payload);
  }

  getDocumentsByDisputeCase(caseType: DisputeCaseType, disputeCaseId: string) {
    return this.getService(caseType).getDocumentsByDisputeCase(disputeCaseId);
  }

  deleteDocument(caseType: DisputeCaseType, id: string) {
    return this.getService(caseType).deleteDocument(id);
  }

  setDocumentPartyAccess(
    caseType: DisputeCaseType,
    partyId: string,
    docId: string,
    canAccess: boolean
  ) {
    return this.getService(caseType).setDocumentPartyAccess(
      partyId,
      docId,
      canAccess
    );
  }
}
